/**
 * @file Input component for date input
 * @author Heino Lenting, Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { DropdownInput } from '../../../base/inputs/input-dropdown/input-dropdown.component';
import * as moment from 'moment';
import { Validator } from 'breeze-client';
import createAutoCorrectedDateTimePipe from '../../pipes/create-datetime.pipe';
import { LocalisationService } from '../../../core/localisation.service';
/**
 * Class for input component for datetime input
 */
var InputDateTimeComponent = /** @class */ (function (_super) {
    tslib_1.__extends(InputDateTimeComponent, _super);
    /**
     * Constructs date time input editor
     * @param localisationService Localization service to support multi language.
     */
    function InputDateTimeComponent(localisationService) {
        var _this = _super.call(this) || this;
        _this.localisationService = localisationService;
        _this.placeholderKey = 'APP.INPUTS.DATETIME_PLACEHOLDER';
        _this.linkedDate = null;
        _this.allowedStates = ['year', 'month', 'day', 'hours', 'minutes'];
        _this.initialState = 'day';
        /**
         * Dummy moment object, to use for localization
         *
         * @type {moment.Moment}
         */
        _this.dayMoment = moment();
        /**
         * Whether the infinite button should be displayed
         * @type {boolean}
         */
        _this.allowInfinity = false;
        /**
         * Whether the today button should be displayed
         * @type {boolean}
         */
        _this.allowToday = true;
        _this._displayDate = moment();
        return _this;
    }
    Object.defineProperty(InputDateTimeComponent.prototype, "format", {
        /**
         * Sets the datetime format
         */
        set: function (value) {
            if (value) {
                if (value.allowInfinity != null) {
                    this.allowInfinity = value.allowInfinity;
                }
                if (value.allowToday != null) {
                    this.allowToday = value.allowToday;
                }
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InputDateTimeComponent.prototype, "displayDate", {
        get: function () {
            if (this._displayDate.isValid()) {
                return this._displayDate;
            }
            else {
                return moment();
            }
        },
        set: function (value) {
            if (value.isValid()) {
                this._displayDate = value;
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InputDateTimeComponent.prototype, "formatString", {
        get: function () {
            return this.localisationService.dateTime.format;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InputDateTimeComponent.prototype, "mask", {
        get: function () {
            return LocalisationService.toMaskArray(this.localisationService.dateTime.mask);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InputDateTimeComponent.prototype, "pipe", {
        get: function () {
            return createAutoCorrectedDateTimePipe(this.localisationService.dateTime.correctionFormat);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InputDateTimeComponent.prototype, "valueAsText", {
        get: function () {
            return this.valueText;
        },
        set: function (value) {
            this.setValueFromString(value);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InputDateTimeComponent.prototype, "valueAsMoment", {
        /** Gets the value of the input component as moment. */
        get: function () {
            if (moment.isMoment(this.value)) {
                return this.value;
            }
            if (moment.isDate(this.value)) {
                return moment(this.value);
            }
            return null;
        },
        /** Sets the value of the input component as moment. */
        set: function (val) {
            if (!moment.isMoment(val)) {
                return;
            }
            this.value = val.toDate();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * User leaves input field (blur)
     */
    InputDateTimeComponent.prototype.leaveViaBlur = function () {
        var _this = this;
        this.valueText = null;
        setTimeout(function () {
            if (_this.value && (moment.isDate(_this.value) || moment.isMoment(_this.value))) {
                _this.valueText = moment(_this.value).format(_this.formatString);
            }
            else {
                _this.valueText = '';
            }
            // Workaround angular bug?
            // Needed when using input without fieldset.
            _this.control.reset(_this.value);
        }, 0);
    };
    /**
     * The default control validator for date time.
     */
    InputDateTimeComponent.prototype.defaultControlValidator = function () {
        var self = this;
        return function (c) {
            return self.isValidValue(c.value);
        };
    };
    /**
     * Validates whether the parameter is a Moment object or can parsed into one
     * @protected
     * @param {*} newValue The new value
     * @returns {ValidationErrors} has errors if value is false
     * @memberOf InputDateTimeComponent
     */
    InputDateTimeComponent.prototype.isValidValue = function (newValue) {
        var _a;
        var errorKey = Validator.date().name;
        var validationError = (_a = {},
            _a[errorKey] = Validator.messageTemplates[errorKey],
            _a);
        if (newValue === undefined || newValue === null || newValue === '') {
            return !this.required ? validationError : null;
        }
        if (typeof newValue === 'string') {
            var result = this.parseValue(newValue);
            if (!result) {
                return validationError;
            }
            return !result.isValid() ? validationError : null;
        }
        return !moment.isMoment(newValue) ? validationError : null;
    };
    /**
     * Parses a string into a Moment object
     * @param newValue The string to be parsed
     * @returns The moment object parsed from the parameter
     */
    InputDateTimeComponent.prototype.parseValue = function (newValue) {
        if (moment.isMoment(newValue)) {
            return newValue;
        }
        if (typeof newValue === 'string' && newValue !== '') {
            var result = moment(newValue, this.formatString, true);
            if (result === null || !result.isValid()) {
                return null;
            }
            else {
                return result;
            }
        }
        return null;
    };
    /**
     * Switches the dropdown between open or closed state; goes to the initial template state if the dropdown is opened
     */
    InputDateTimeComponent.prototype.toggleDropdown = function () {
        if (!this.isOpen()) {
            this.changeState(this.initialState);
        }
        this.toggle();
    };
    /**
     * Used to close the dropdown
     */
    InputDateTimeComponent.prototype.setClose = function () {
        _super.prototype.setClose.call(this);
        if (this.control) {
            // TODO: Uitzoeken waarom dit niet nodig is bij de InputColorComponent.
            this.control.updateValueAndValidity();
        }
    };
    /**
     * Converts the datetime value to a string according to the format
     * @returns The string representation of the datetime value
     */
    InputDateTimeComponent.prototype.getDateString = function () {
        if (!moment.isMoment(this.valueAsMoment)) {
            return '';
        }
        if (this.isValueInfinity()) {
            return 'Infinity';
        }
        return this.valueAsMoment.format(this.formatString);
    };
    /**
     * Checks if the datetime value is set to infinity
     * @returns A value indicating whether the datetime value is set to infinity
     */
    InputDateTimeComponent.prototype.isValueInfinity = function () {
        return moment.isMoment(this.valueAsMoment) && this.valueAsMoment.toDate().getUTCFullYear() === 9999;
    };
    /**
     * Checks if the date component of the value is today
     * @returns A value indicating whether set date is today
     */
    InputDateTimeComponent.prototype.isValueToday = function () {
        return this.isDate(moment(), 'day');
    };
    /**
     * Checks if the parameter is today
     * @param date The date is compared with today
     * @returns A value indicating whether the parameter is today
     */
    InputDateTimeComponent.prototype.isToday = function (date) {
        if (!moment.isMoment(date)) {
            return false;
        }
        return date.clone().startOf('day').isSame(moment().startOf('day'));
    };
    /**
     * Checks whether the given date equals the date of the value
     * @param date The date is compared with the value
     * @param unitOfTime The precision of the comparison
     * @returns A value indicating whether the date parameter is equal to the value up to the precision of the unit of time parameter
     */
    InputDateTimeComponent.prototype.isDate = function (date, unitOfTime) {
        if (!moment.isMoment(this.valueAsMoment)) {
            return false;
        }
        var testDate = date.clone().startOf(unitOfTime);
        var thisDate = this.valueAsMoment.clone().startOf(unitOfTime);
        return thisDate.isSame(testDate);
    };
    /**
     * Checks whether the given time equals the time of the value
     * @param time The hours or minutes is compared with the same component of the value
     * @param unitOfTime The unit of time to be compared
     * @returns A value indicating whether the time parameter is equal to the same unit of time of the value
     */
    InputDateTimeComponent.prototype.isTime = function (time, unitOfTime) {
        return moment.isMoment(this.valueAsMoment) && this.valueAsMoment.get(unitOfTime) === time;
    };
    /**
     * Checks whether the given date falls in range
     * @param date The date that falls or does not fall in range
     * @returns A value indicating whether the parameter falls in range of the selected date and
     * the date to which the datetime component is linked
     */
    InputDateTimeComponent.prototype.inRange = function (date) {
        if (!moment.isMoment(this.valueAsMoment) ||
            !moment.isMoment(this.linkedDate) ||
            this.isValueInfinity() ||
            this.linkedDate.year() === 9999) {
            return false;
        }
        return (date > this.valueAsMoment && date <= this.linkedDate) || (date < this.valueAsMoment && date >= this.linkedDate);
    };
    /**
     * Sets the datetime value to today and goes to the 'hours' template state or closes the dropdown
     */
    InputDateTimeComponent.prototype.setToday = function () {
        this.setDate(moment().startOf('day'), 'day');
    };
    /**
     * Sets the datetime value to 9999-12-31 and closes the dropdown
     */
    InputDateTimeComponent.prototype.setInfinity = function () {
        // Makes 9999/12/31 +01:00 moment independent of time-zone client.
        this.valueAsMoment = moment(new Date(Date.UTC(9999, 11, 30, 23)));
        this.setClose();
    };
    /**
     * Sets the date component of the value
     * @param date The date to which the value is changed
     * @param unitOfTime The unit of time that was selected - the template state
     */
    InputDateTimeComponent.prototype.setDate = function (date, unitOfTime) {
        // this.valueAsMoment = date.clone().startOf(unitOfTime);
        // this.value = date.startOf(unitOfTime).toDate();
        if (moment.isMoment(this.valueAsMoment)) {
            date.hours(this.valueAsMoment.hours());
            date.minutes(this.valueAsMoment.minutes());
        }
        this.valueAsText = date.format(this.formatString);
        this.nextTemplateStateFrom(unitOfTime);
    };
    /**
     * Sets the time component of the value
     * @param time The hours or minutes to which the time component of the value is changed
     * @param unitOfTime The unit of time to be set - the template state
     */
    InputDateTimeComponent.prototype.setTime = function (time, unitOfTime) {
        var date = moment.isMoment(this.valueAsMoment) ? this.valueAsMoment.clone() : moment();
        date = date.set(unitOfTime, time).startOf(unitOfTime);
        this.valueAsText = date.format(this.formatString);
        this.nextTemplateStateFrom(unitOfTime);
    };
    /**
     * Checks whether the given template state is available
     * @param state The template state
     * @returns A value indicating whether the state parameter is an allowed template state.
     */
    InputDateTimeComponent.prototype.isAllowed = function (state) {
        return this.allowedStates.indexOf(state) !== -1;
    };
    /**
     * Changes the dropdown template state
     * @param state The template state
     */
    InputDateTimeComponent.prototype.changeState = function (state) {
        if (!this.isAllowed(state)) {
            return;
        }
        if (moment.isMoment(this.valueAsMoment) && this.valueAsMoment.year() === 9999) {
            this.displayDate = moment();
        }
        else if (moment.isMoment(this.valueAsMoment)) {
            this.displayDate = this.valueAsMoment.clone();
        }
        else {
            this.displayDate = moment();
        }
        /*
                this.displayDate = !moment.isMoment(this.valueAsMoment) || this.valueAsMoment.year() === 9999
                    ? moment() : this.valueAsMoment.clone();
        */
        switch (state) {
            case 'year':
                this.setDisplayYears();
                break;
            case 'month':
                this.setDisplayMonths();
                break;
            case 'day':
                this.setDisplayWeeks();
                break;
            default:
                break;
        }
        this.state = state;
    };
    /**
     * Changes the year to be displayed in the dropdown
     * @param years The year is displayed in the dropdown
     */
    InputDateTimeComponent.prototype.changeDisplayYear = function (years) {
        this.displayDate.add(years, 'year');
        this.setDisplayYears();
        this.setDisplayMonths();
    };
    /**
     * Moves the displayed month forwards or backwards
     * @param months The number of months the displayed date is moved
     */
    InputDateTimeComponent.prototype.changeDisplayMonth = function (months) {
        this.displayDate.add(months, 'month');
        this.setDisplayWeeks();
    };
    /**
     * Set the value of the input.
     * Override this method when extending the value behavior.
     * @param val The new value to set.
     */
    InputDateTimeComponent.prototype.setValue = function (val) {
        _super.prototype.setValue.call(this, val);
        this.valueText = moment(this.value).format(this.formatString);
    };
    /**
     * Accepts a string, which is parsed to a Javascript data object.
     * Display and subtitle are also updated
     * @param value A parsable value for the date time
     */
    InputDateTimeComponent.prototype.setValueFromString = function (value) {
        // Only the User should provoke this.
        // It is not the user when: value == null or value == undefined
        if (value !== null && typeof value !== 'undefined') {
            this.valueText = value;
            var parsedValue = this.parseValue(value);
            if (parsedValue) {
                this.safeTranslateFromString(parsedValue);
            }
        }
    };
    /**
     * Safe translate string date to value.
     * Is needed for InputDateComponent when used in different time zones.
     * @protected
     * @param {Moment} translated The value translated from the string.
     */
    InputDateTimeComponent.prototype.safeTranslateFromString = function (translated) {
        this.value = translated.toDate();
    };
    /**
     * Moves to the next template state or closes the dropdown
     * @param unitOfTime The current template
     */
    InputDateTimeComponent.prototype.nextTemplateStateFrom = function (unitOfTime) {
        var state = this.allowedStates.indexOf(unitOfTime);
        if (this.allowedStates.length > state + 1) {
            this.changeState(this.allowedStates[state + 1]);
        }
        else {
            this.setClose();
        }
    };
    /**
     * Sets the years to be displayed in the 'year' template state
     */
    InputDateTimeComponent.prototype.setDisplayYears = function () {
        var decade = this.displayDate.year();
        decade -= decade % 10;
        this.displayYears = [];
        for (var year = decade; year < decade + 12; year += 1) {
            this.displayYears.push(moment({ year: year, month: 0, day: 1 }));
        }
    };
    /**
     * Sets the months to be displayed in the 'month' template state
     */
    InputDateTimeComponent.prototype.setDisplayMonths = function () {
        var year = this.displayDate.year();
        this.displayMonths = [];
        for (var month = 0; month < 12; month += 1) {
            this.displayMonths.push(moment({ year: year, month: month, day: 1 }));
        }
    };
    /**
     * Sets the weeks to be displayed in the 'day' template state
     */
    InputDateTimeComponent.prototype.setDisplayWeeks = function () {
        var day = moment([this.displayDate.year(), this.displayDate.month(), 1]);
        var lastDay = moment([this.displayDate.year(), this.displayDate.month(), this.displayDate.daysInMonth()]);
        day.day(day.day() === 0 ? -6 : 1);
        this.displayWeeks = [];
        while (day <= lastDay) {
            var week = { number: day.isoWeek(), days: [] };
            for (var d = 0; d < 7; d += 1) {
                week.days.push(day.clone().add(d, 'day'));
            }
            this.displayWeeks.push(week);
            day.add(7, 'day');
        }
    };
    return InputDateTimeComponent;
}(DropdownInput));
export { InputDateTimeComponent };
